// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { STATUS_LABELS, STATUS_ORDER } from '../../../utils/statusPresentation';
import { StatusMark } from './primitives/StatusMark';
import { BreakerGlyph } from './conductors/BreakerGlyph';
import { BREAKER_ARC_R, BREAKER_REACH } from '../../../utils/powerFlowBreakers';
import type { BreakerState } from '../../../utils/powerFlowLive';

/**
 * One breaker glyph at legend size, in one position, drawn along a short
 * horizontal run so it sits in the sample column like the conductor rules.
 * The run is two pieces that stop at the contacts, the way the edge layer
 * cuts it; the alarm mark hangs below, as it does on a horizontal run.
 */
const LegendBreaker = ({
  position,
  alarm = false,
}: {
  position: BreakerState['position'];
  alarm?: boolean;
}) => {
  const reach = BREAKER_REACH + 2;
  const top = -9;
  const bottom = alarm ? BREAKER_ARC_R + 20 : 9;
  // Same width in every position; the ALARM word overflows the box (`overflow: visible`).
  const right = reach;
  return (
    <svg
      className={`sld-legend__breaker${alarm ? ' sld-legend__breaker--alarm' : ''} sld-breaker sld-breaker--${position}`}
      viewBox={`${-reach} ${top} ${right + reach} ${bottom - top}`}
      width={(right + reach) * 0.75}
      height={(bottom - top) * 0.75}
      aria-hidden="true"
      focusable="false"
    >
      <line x1={-reach} y1="0" x2={-BREAKER_ARC_R} y2="0" className="sld-edge" strokeWidth={1.2} />
      <line x1={BREAKER_ARC_R} y1="0" x2={reach} y2="0" className="sld-edge" strokeWidth={1.2} />
      <BreakerGlyph position={position} alarm={alarm} horizontal />
    </svg>
  );
};

export interface SldLegendProps {
  /** Whether the drawing contains any bus tie. */
  hasTies: boolean;
  /** Whether any repeated terminal load has been summarised into a count. */
  hasGroups: boolean;
  /**
   * Which live and inferred treatments are on screen, so each is keyed only
   * when a reader can see it. All default to off.
   */
  paint?: Partial<SldLegendPaint>;
  /** False hides every inferred row, as the page's toggle does. */
  showInferred?: boolean;
}

export interface SldLegendPaint {
  /** A measured de-energized run. */
  dead: boolean;
  /** A standby run — inferred from a source exporting nothing. */
  standby: boolean;
  /** The flow overlay. */
  flowing: boolean;
  /** A dotted run: not carrying load. */
  idle: boolean;
  /** A maintenance bypass. */
  bypass: boolean;
  /** Block enclosures. */
  blocks: boolean;
  /** Any device is showing an inferred state word. */
  inferredStates: boolean;
  /** Some value on screen is stale. */
  stale: boolean;
  /** A bus is carrying a meter block. */
  meters: boolean;
  /** A conductor carries a breaker mark. */
  breakers: boolean;
  /** A breaker is in alarm. */
  breakerAlarm: boolean;
}

/**
 * Key for the diagram.
 *
 * Mirrors the chip and conductor treatment exactly, glyphs included, so the
 * legend explains what is actually on screen rather than an idealised version.
 * That is why the symbol rows are derived from the model: keying a symbol the
 * drawing does not contain sends the reader looking for it.
 *
 * The status rows use `StatusMark`, the same component the chips and bars do,
 * so the key cannot drift from the marks it explains — which is exactly what
 * happened while four files each carried their own copy of that ternary.
 */
/** One row of the key: the sample on the left, its meaning on the right. */
const Row = ({
  className,
  sample,
  children,
}: {
  className: string;
  sample: React.ReactNode;
  children: React.ReactNode;
}) => (
  <li className={`sld-legend__item ${className}`}>
    <span className="sld-legend__sample" aria-hidden="true">
      {sample}
    </span>
    <span className="sld-legend__label">{children}</span>
  </li>
);

/** A titled column of rows. Renders nothing when every row was withheld. */
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => {
  const rows = React.Children.toArray(children).filter(Boolean);
  if (rows.length === 0) return null;
  return (
    <section className="sld-legend__section" aria-label={title}>
      <h4 className="sld-legend__heading">{title}</h4>
      <ul className="sld-legend__rows">{rows}</ul>
    </section>
  );
};

export const SldLegend = ({ hasTies, hasGroups, paint = {}, showInferred }: SldLegendProps) => {
  const inferred = showInferred !== false;
  return (
    <div className="sld-legend">
      <Section title="Status">
        {STATUS_ORDER.map(status => (
          <Row
            key={status}
            className={`sld-legend__item--${status}`}
            sample={
              <span className="sld-legend__marker">
                <StatusMark status={status} />
              </span>
            }
          >
            {STATUS_LABELS[status]}
          </Row>
        ))}
      </Section>

      {/*
        The primitives, not a symbol per equipment type.
        A bar is a bus, where everything tapped off it sits at one potential; a
        device sits in series on the run, drawn as its one-line symbol when it has
        one and as a box when it has none. The symbols themselves are
        keyed by the mapping document, not here — a reader who knows one-lines
        knows them, and one who does not needs the document, not a row.
      */}
      <Section title="Elements">
        <Row className="sld-legend__item--primitive" sample={<span className="sld-legend__bar" />}>
          Bus &mdash; one potential
        </Row>
        <Row className="sld-legend__item--primitive" sample={<span className="sld-legend__box" />}>
          Device &mdash; its symbol on the run, a box when it has none
        </Row>
        {paint.meters && (
          <Row
            className="sld-legend__item--meter"
            sample={<span className="sld-legend__meter">480 V</span>}
          >
            Meter on the bus
          </Row>
        )}
        {hasGroups && (
          <Row className="sld-legend__item--group" sample={<span className="sld-legend__group" />}>
            Identical loads, grouped &mdash; select to expand
          </Row>
        )}
        {inferred && paint.blocks && (
          <Row className="sld-legend__item--block" sample={<span className="sld-legend__group" />}>
            Block &mdash; from labels
          </Row>
        )}
      </Section>

      {paint.breakers && (
        <Section title="Breakers">
          <Row className="sld-legend__item--breaker" sample={<LegendBreaker position="closed" />}>
            Closed
          </Row>
          <Row className="sld-legend__item--breaker" sample={<LegendBreaker position="open" />}>
            Open &mdash; arc off the contact
          </Row>
          <Row className="sld-legend__item--breaker" sample={<LegendBreaker position="unknown" />}>
            Unknown &mdash; dashed
          </Row>
          {paint.breakerAlarm && (
            <Row
              className="sld-legend__item--breaker-alarm"
              sample={<LegendBreaker position="closed" alarm />}
            >
              In alarm &mdash; select for details
            </Row>
          )}
        </Section>
      )}

      <Section title="Conductors">
        <Row
          className="sld-legend__item--mv"
          sample={<span className="sld-legend__rule sld-legend__rule--mv" />}
        >
          Medium voltage
        </Row>
        <Row
          className="sld-legend__item--lv"
          sample={<span className="sld-legend__rule sld-legend__rule--lv" />}
        >
          Low voltage
        </Row>
        {hasTies && (
          <Row
            className="sld-legend__item--tie"
            sample={<span className="sld-legend__rule sld-legend__rule--tie" />}
          >
            Bus tie
          </Row>
        )}
        {paint.dead && (
          <Row
            className="sld-legend__item--dead"
            sample={<span className="sld-legend__rule sld-legend__rule--dead" />}
          >
            Off &mdash; measured de-energized
          </Row>
        )}
        {inferred && paint.flowing && (
          <Row
            className="sld-legend__item--flow"
            sample={<span className="sld-legend__rule sld-legend__rule--flow" />}
          >
            Carrying load &mdash; from measured power
          </Row>
        )}
        {inferred && paint.idle && (
          <Row
            className="sld-legend__item--idle"
            sample={<span className="sld-legend__rule sld-legend__rule--idle" />}
          >
            Dotted &mdash; not carrying load
          </Row>
        )}
      </Section>

      {/*
        Live and inferred paint. Each row appears only when the drawing carries
        the treatment, and each inferred row says so in its label: the legend is
        where a reader learns that a dashed grey run is a claim we made from a
        zero reading, not a fact the twin published.
      */}
      <Section title="Inferred">
        {inferred && paint.inferredStates && (
          <Row
            className="sld-legend__item--inferred"
            sample={<span className="sld-legend__inferred">Standby</span>}
          >
            Italic state &mdash; inferred, hover for the rule
          </Row>
        )}
        {inferred && paint.standby && (
          <Row
            className="sld-legend__item--standby"
            sample={<span className="sld-legend__rule sld-legend__rule--dead" />}
          >
            Standby &mdash; inferred from P = 0
          </Row>
        )}
        {inferred && paint.bypass && (
          <Row
            className="sld-legend__item--bypass"
            sample={<span className="sld-legend__rule sld-legend__rule--dead" />}
          >
            Bypass &mdash; inferred from parallel path
          </Row>
        )}
        {paint.stale && (
          <Row
            className="sld-legend__item--stale"
            sample={<span className="sld-legend__stale">0.97</span>}
          >
            Stale reading
          </Row>
        )}
      </Section>
    </div>
  );
};
